"use client";

import { type KeyboardEvent, type ReactNode, useRef } from "react";
import { cn } from "../lib/cn";

export interface TabItem {
  readonly id: string;
  readonly label: string;
}

/**
 * Tabs within a page (docs/design/meridian.md): a 2 px accent line under the selected one. Arrow keys, Home, and End
 * move between them and select, as the ARIA tabs pattern does. Each panel is a `TabPanel` with the same `idPrefix`.
 */
export function Tabs({
  label,
  items,
  value,
  onChange,
  idPrefix,
  className,
}: {
  label: string;
  items: readonly TabItem[];
  value: string;
  onChange: (id: string) => void;
  idPrefix: string;
  className?: string;
}) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const move = (event: KeyboardEvent, index: number) => {
    const last = items.length - 1;
    const next =
      event.key === "ArrowRight"
        ? (index + 1) % items.length
        : event.key === "ArrowLeft"
          ? (index + last) % items.length
          : event.key === "Home"
            ? 0
            : event.key === "End"
              ? last
              : undefined;
    if (next === undefined) return;
    event.preventDefault();
    const target = items[next];
    if (target === undefined) return;
    onChange(target.id);
    refs.current[next]?.focus();
  };
  return (
    <div role="tablist" aria-label={label} className={cn("flex gap-6 border-b border-border", className)}>
      {items.map((item, index) => {
        const selected = item.id === value;
        return (
          <button
            key={item.id}
            ref={(element) => {
              refs.current[index] = element;
            }}
            type="button"
            role="tab"
            id={`${idPrefix}-tab-${item.id}`}
            aria-controls={`${idPrefix}-panel-${item.id}`}
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(item.id)}
            onKeyDown={(event) => move(event, index)}
            className={cn(
              "-mb-px border-b-2 pb-2.5 text-body transition-colors duration-(--duration-fast)",
              selected
                ? "border-brand-600 font-semibold text-ink"
                : "border-transparent font-medium text-ink-muted hover:text-ink",
            )}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}

export function TabPanel({
  idPrefix,
  id,
  children,
  className,
}: {
  idPrefix: string;
  id: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      role="tabpanel"
      id={`${idPrefix}-panel-${id}`}
      aria-labelledby={`${idPrefix}-tab-${id}`}
      className={className}
    >
      {children}
    </div>
  );
}

/** A segmented control: a `muted` track with the selected item a raised pill, for two to four views of one thing. */
export function SegmentedControl({
  label,
  items,
  value,
  onChange,
  className,
}: {
  label: string;
  items: readonly TabItem[];
  value: string;
  onChange: (id: string) => void;
  className?: string;
}) {
  return (
    <fieldset
      aria-label={label}
      className={cn("inline-flex gap-0.5 rounded-(--radius-control) bg-muted p-0.75", className)}
    >
      {items.map((item) => {
        const selected = item.id === value;
        return (
          <button
            key={item.id}
            type="button"
            aria-pressed={selected}
            onClick={() => onChange(item.id)}
            className={cn(
              "h-7 rounded-(--radius-chip) px-3 text-small font-medium whitespace-nowrap transition-colors duration-(--duration-fast)",
              selected ? "bg-surface text-ink shadow-raised" : "text-ink-muted hover:text-ink",
            )}
          >
            {item.label}
          </button>
        );
      })}
    </fieldset>
  );
}

/**
 * Two to four views drawn as the segmented control but announced as tabs (cards 112 and 113), for a switch whose
 * views replace the content below it: Buying and Selling, Email and Mobile.
 */
export function SegmentedTabs({
  label,
  items,
  value,
  onChange,
  className,
}: {
  label: string;
  items: readonly TabItem[];
  value: string;
  onChange: (id: string) => void;
  className?: string;
}) {
  return (
    <div
      role="tablist"
      aria-label={label}
      className={cn("inline-flex gap-0.5 self-start rounded-(--radius-control) bg-muted p-0.75", className)}
    >
      {items.map((item) => {
        const selected = item.id === value;
        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={selected}
            onClick={() => onChange(item.id)}
            className={cn(
              "h-7 flex-1 rounded-(--radius-chip) px-3 text-small font-medium whitespace-nowrap transition-colors duration-(--duration-fast)",
              selected ? "bg-surface text-ink shadow-raised" : "text-ink-muted hover:text-ink",
            )}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}
